import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api.js";

export interface AutomationCard {
  id: number;
  key: string;
  name: string;
  description: string | null;
  triggerType: string;
  triggerLabel: string;
  storeScope: string[] | null;
  actionKind: "template" | "bot";
  botAction: string | null;
  category: "utility" | "marketing" | "bot";
  enabled: boolean;
  runsTotal: number;
  runsToday: number;
  lastRunAt: string | null;
  templateId: number | null;
  templateName: string | null;
  templateStatus: string | null;
}

export interface AutomationRun {
  id: number;
  status: string;
  createdAt: string;
  automationName: string | null;
  contactPhone: string | null;
  contactName: string | null;
  orderRef: string | null;
  orderNumber: string | null;
}

export function useAutomations(businessId: string) {
  return useQuery<AutomationCard[]>({
    queryKey: [businessId, "automations"],
    queryFn: async () => {
      const r = await apiFetch<{ automations: AutomationCard[] }>(
        `/api/v1/automations?business=${businessId}`,
      );
      return r.automations;
    },
  });
}

export function useAutomationRuns(businessId: string) {
  return useQuery<AutomationRun[]>({
    queryKey: [businessId, "automation-runs"],
    queryFn: async () => {
      const r = await apiFetch<{ runs: AutomationRun[] }>(
        `/api/v1/automations/runs?business=${businessId}`,
      );
      return r.runs;
    },
  });
}

export function useToggleAutomation(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; enabled: boolean }) =>
      apiFetch<{ id: number; enabled: boolean }>(
        `/api/v1/automations/${vars.id}?business=${businessId}`,
        { method: "PATCH", body: JSON.stringify({ enabled: vars.enabled }) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [businessId, "automations"] }),
  });
}
